if (!customElements.get('collection-ajax-experience')) {
  customElements.define('collection-ajax-experience', class CollectionAjaxExperience extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.sectionId = this.dataset.sectionId;
      this.collectionView = this.dataset.collectionView || 'collection-page-2';
      this.collectionUrl = this.dataset.collectionUrl || window.location.pathname;
      this.collectionHandle = this.dataset.collectionHandle || this.collectionHandleFromUrl(this.collectionUrl);
      this.shellPath = this.dataset.shellCollectionUrl || window.location.pathname;
      this.shellCollectionHandle = this.dataset.shellCollectionHandle || this.collectionHandle;
      this.shellCollectionTitle = this.dataset.shellCollectionTitle || '';
      this.currentRenderedParams = this.collectionParamsFromUrl(new URL(window.location.href)).toString();
      this.loading = this.querySelector('[data-collection-loading]');
      this.status = this.querySelector('[data-collection-status]');
      this.abortController = null;
      this.drawerTrigger = null;
      this.responseCache = new Map();
      this.inflightRequests = new Map();
      this.maxCacheEntries = 16;

      const initialUrl = new URL(window.location.href);
      const requestedInitialHandle =
        initialUrl.searchParams.get('collection') || this.collectionHandle;

      if (requestedInitialHandle !== this.collectionHandle) {
        this.setAttribute('data-initial-collection-pending', 'true');
      }

      this.normalizeCollectionChips(this, requestedInitialHandle);

      this.onclick = (event) => this.handleClick(event);
      this.onchange = (event) => this.handleChange(event);
      this.onsubmit = (event) => this.handleSubmit(event);
      this.onkeydown = (event) => this.handleKeydown(event);
      this.onpointerover = (event) => this.handleCollectionIntent(event);
      this.onfocusin = (event) => this.handleCollectionIntent(event);

      requestAnimationFrame(() => {
        this.restoreFromLocation();
        this.scheduleAdjacentPrefetch();
      });
    }

    handleSubmit(event) {
      if (!event.target.closest('[data-collection-form]')) return;
      event.preventDefault();
      this.render(this.paramsFromControls());
    }

    handleCollectionIntent(event) {
      const link = event.target.closest('[data-collection-link]');
      if (!link || !this.contains(link)) return;
      this.prefetchCollection(link);
    }

    handleClick(event) {
      const collectionLink = event.target.closest('[data-collection-link]');
      if (collectionLink && this.contains(collectionLink)) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.navigateCollection(collectionLink.href, collectionLink.dataset.preserveFilters === 'true');
        return;
      }

      const scrollButton = event.target.closest('[data-occasion-scroll]');
      if (scrollButton) {
        this.querySelector('[data-occasion-rail]')?.scrollBy({
          left: scrollButton.dataset.occasionScroll === 'left' ? -300 : 300,
          behavior: 'smooth'
        });
        return;
      }

      const removeFilter = event.target.closest('[data-remove-filter]');
      if (removeFilter) {
        const params = this.currentParams();
        const values = params.getAll(removeFilter.dataset.filterParam).filter((item) => item !== removeFilter.dataset.filterValue);
        params.delete(removeFilter.dataset.filterParam);
        values.forEach((item) => params.append(removeFilter.dataset.filterParam, item));
        params.delete('page');
        this.render(params);
        return;
      }

      const removePrice = event.target.closest('[data-remove-price-filter]');
      if (removePrice) {
        const params = this.currentParams();
        params.delete(removePrice.dataset.minParam);
        params.delete(removePrice.dataset.maxParam);
        params.delete('page');
        this.render(params);
        return;
      }

      if (event.target.closest('[data-clear-filters]')) {
        this.render(this.clearFilterParams());
        return;
      }
      if (event.target.closest('[data-mobile-filter-open]')) {
        this.openDrawer(event.target.closest('[data-mobile-filter-open]'));
        return;
      }
      if (event.target.closest('[data-mobile-filter-close]')) {
        this.closeDrawer();
        return;
      }
      if (event.target.closest('[data-mobile-filter-apply]')) {
        this.closeDrawer(false);
        this.render(this.paramsFromControls());
        return;
      }
      if (event.target.closest('[data-price-apply]')) {
        this.render(this.paramsFromControls());
        return;
      }

      const loadMore = event.target.closest('[data-load-more]');
      if (loadMore) this.loadMore(loadMore);
    }

    handleChange(event) {
      if (event.target.matches('[data-collection-sort]')) {
        const params = this.currentParams();
        params.set('sort_by', event.target.value);
        params.delete('page');
        this.render(params);
        return;
      }

      if (event.target.matches('[data-collection-filter]') && event.target.type !== 'number') {
        if (event.target.closest('[data-mobile-filter-drawer]')) return;
        this.render(this.paramsFromControls());
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape') this.closeDrawer();
      if (event.key !== 'Tab') return;

      const drawer = this.querySelector('[data-mobile-filter-drawer]:not([hidden])');
      if (!drawer) return;

      const panel = drawer.querySelector('[role="dialog"]');
      const focusables = [...panel.querySelectorAll('button, a, input, select, summary, [tabindex]:not([tabindex="-1"])')]
        .filter((element) => !element.disabled && !element.hidden);

      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }

      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    collectionParamsFromUrl(url) {
      const params = new URLSearchParams();
      url.searchParams.forEach((value, key) => {
        if (key.startsWith('filter.') || key === 'sort_by' || key === 'page') params.append(key, value);
      });
      return params;
    }

    currentParams() {
      return this.collectionParamsFromUrl(new URL(window.location.href));
    }

    clearFilterParams() {
      const params = this.currentParams();
      [...params.keys()].forEach((key) => {
        if (key.startsWith('filter.') || key === 'page') params.delete(key);
      });
      return params;
    }

    paramsFromControls() {
      const params = this.clearFilterParams();
      const seen = new Set();

      this.querySelectorAll('[data-collection-filter]').forEach((control) => {
        const key = `${control.name}:${control.value}`;
        if (seen.has(key)) return;
        seen.add(key);

        if (control.type === 'checkbox') {
          if (control.checked && !control.disabled) params.append(control.name, control.value);
        } else if (control.value !== '') {
          params.set(control.name, control.value);
        }
      });

      params.delete('page');
      return params;
    }

    compatibleParams() {
      const safe = new URLSearchParams();
      this.currentParams().forEach((value, key) => {
        if (
          key === 'sort_by' ||
          key.includes('.interest') ||
          key.includes('.brand') ||
          key.includes('product_type') ||
          key.includes('availability') ||
          key.includes('price') ||
          key.includes('personal')
        ) {
          safe.append(key, value);
        }
      });
      return safe;
    }

    collectionHandleFromUrl(urlValue) {
      const url = new URL(urlValue, window.location.origin);
      const parts = url.pathname.split('/').filter(Boolean);
      const collectionsIndex = parts.indexOf('collections');
      return collectionsIndex >= 0 ? parts[collectionsIndex + 1] || '' : '';
    }

    normalizeCollectionChips(root = this, activeHandle = '') {
      const seenHandles = new Set();
      const currentHandle = activeHandle || root.dataset?.collectionHandle || this.collectionHandle || '';

      root.querySelectorAll('[data-collection-link]').forEach((link) => {
        const handle =
          link.dataset.collectionHandle || this.collectionHandleFromUrl(link.href);
        const duplicate = Boolean(handle && seenHandles.has(handle));

        if (handle && !duplicate) seenHandles.add(handle);

        link.hidden = duplicate;

        const active = !duplicate && Boolean(handle) && handle === currentHandle;
        link.classList.toggle('is-active', active);

        if (active) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
      });
    }

    findCollectionLink(handle) {
      return [...this.querySelectorAll('[data-collection-link]')]
        .find((link) => this.collectionHandleFromUrl(link.href) === handle);
    }

    buildRequestUrl(collectionUrl, params) {
      const requestUrl = new URL(collectionUrl, window.location.origin);
      requestUrl.search = '';
      params.forEach((value, key) => requestUrl.searchParams.append(key, value));
      requestUrl.searchParams.set('view', this.collectionView);
      requestUrl.searchParams.set('section_id', this.sectionId);
      return requestUrl;
    }

    buildPublicUrl(params, collectionHandle) {
      const publicUrl = new URL(window.location.href);
      publicUrl.pathname = this.shellPath;

      [...publicUrl.searchParams.keys()].forEach((key) => {
        if (key === 'collection' || key === 'sort_by' || key === 'page' || key.startsWith('filter.')) {
          publicUrl.searchParams.delete(key);
        }
      });

      params.forEach((value, key) => publicUrl.searchParams.append(key, value));

      if (collectionHandle && collectionHandle !== this.shellCollectionHandle) {
        publicUrl.searchParams.set('collection', collectionHandle);
      }

      return publicUrl;
    }

    async navigateCollection(href, preserveFilters) {
      const target = new URL(href, window.location.origin);
      const params = preserveFilters ? this.compatibleParams() : new URLSearchParams();
      params.delete('page');
      const handle = this.collectionHandleFromUrl(target);
      this.setActiveCollectionChip(handle);
      await this.fetchCollection(target, params, true);
    }

    async fetchCollection(targetUrl, params, pushState) {
      const collectionHandle = this.collectionHandleFromUrl(targetUrl);
      const requestUrl = this.buildRequestUrl(targetUrl.pathname, params);
      const publicUrl = this.buildPublicUrl(params, collectionHandle);
      await this.fetchAndReplace(requestUrl, publicUrl, pushState);
    }

    async render(params, pushState = true) {
      const requestUrl = this.buildRequestUrl(this.collectionUrl, params);
      const publicUrl = this.buildPublicUrl(params, this.collectionHandle);
      await this.fetchAndReplace(requestUrl, publicUrl, pushState);
    }

    async restoreFromLocation() {
      const currentUrl = new URL(window.location.href);
      const requestedHandle = currentUrl.searchParams.get('collection') || this.shellCollectionHandle;
      const params = this.collectionParamsFromUrl(currentUrl);

      try {
        if (requestedHandle !== this.collectionHandle) {
          const collectionLink = this.findCollectionLink(requestedHandle);
          if (collectionLink) {
            await this.fetchCollection(new URL(collectionLink.href), params, false);
          }
        }
      } finally {
        this.removeAttribute('data-initial-collection-pending');
      }
    }

    setActiveCollectionChip(handle) {
      this.normalizeCollectionChips(this, handle);
    }

    prefetchCollection(link) {
      const target = new URL(link.href, window.location.origin);
      const params = link.dataset.preserveFilters === 'true' ? this.compatibleParams() : new URLSearchParams();
      params.delete('page');
      const requestUrl = this.buildRequestUrl(target.pathname, params);
      this.getSectionHtml(requestUrl).catch(() => {});
    }

    scheduleAdjacentPrefetch() {
      const schedule = window.requestIdleCallback || ((callback) => window.setTimeout(callback, 300));
      schedule(() => {
        const links = [...this.querySelectorAll('[data-collection-link]')].filter((link) => !link.hidden);
        const activeIndex = links.findIndex((link) => link.classList.contains('is-active'));
        const candidates = [links[activeIndex + 1], links[activeIndex - 1]].filter(Boolean);
        candidates.forEach((link) => this.prefetchCollection(link));
      });
    }

    cacheResponse(key, html) {
      if (this.responseCache.has(key)) this.responseCache.delete(key);
      this.responseCache.set(key, html);
      while (this.responseCache.size > this.maxCacheEntries) {
        this.responseCache.delete(this.responseCache.keys().next().value);
      }
    }

    async getSectionHtml(requestUrl, signal) {
      const key = requestUrl.toString();
      if (this.responseCache.has(key)) return this.responseCache.get(key);
      if (this.inflightRequests.has(key)) return this.inflightRequests.get(key);

      const request = fetch(key, {
        signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      }).then(async (response) => {
        if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);
        const html = await response.text();
        this.cacheResponse(key, html);
        return html;
      }).finally(() => {
        this.inflightRequests.delete(key);
      });

      this.inflightRequests.set(key, request);
      return request;
    }

    parseIncoming(html) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      return parsed.querySelector('collection-ajax-experience');
    }

    async fetchAndReplace(requestUrl, publicUrl, pushState) {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      this.setLoading(true);

      try {
        const html = await this.getSectionHtml(requestUrl, this.abortController.signal);
        const incoming = this.parseIncoming(html);
        if (!incoming) throw new Error('The collection section was not returned.');

        // Keep the shell collection navigation stable while AJAX swaps results.
        // Collection page 1 already behaves this way because its shell collection
        // overlaps the incoming "All Cards" target. Non-core landing templates must
        // explicitly retain their shell rail so the collection-page-2 AJAX response
        // cannot inject its own "All Cards" chip.
        const templateSuffix = this.dataset.cardologyCollectionTemplate || '';
        const preserveRailPosition = templateSuffix === 'collection-page-1';
        const preserveShellRail = [
          'christmas_landing_page',
          'harry-potter-lincensed',
          'licensed-landing-pages',
          'storm-trooper-licensed',
          'create-your-own-bundle'
        ].includes(templateSuffix);
        const currentRail = (preserveRailPosition || preserveShellRail)
          ? this.querySelector('[data-occasion-rail]')
          : null;
        const railScrollLeft = currentRail?.scrollLeft || 0;

        if (preserveShellRail && currentRail) {
          const incomingRail = incoming.querySelector('[data-occasion-rail]');
          if (incomingRail) incomingRail.innerHTML = currentRail.innerHTML;
        }

        const currentOriginalLink = this.querySelector('[data-original-collection-link]');
        const incomingOriginalLink = incoming.querySelector('[data-original-collection-link]');

        if (incomingOriginalLink) {
          const originalUrl = this.shellPath;
          const originalTitle =
            this.shellCollectionTitle ||
            currentOriginalLink?.dataset.collectionTitle ||
            currentOriginalLink?.textContent.trim() ||
            incomingOriginalLink.textContent.trim();
          const originalHandle = this.shellCollectionHandle;
          const requestedHandle = this.collectionHandleFromUrl(requestUrl);
          const originalIsActive = requestedHandle === originalHandle;

          incomingOriginalLink.href = originalUrl;
          incomingOriginalLink.textContent = originalTitle;
          incomingOriginalLink.dataset.collectionHandle = originalHandle;
          incomingOriginalLink.dataset.collectionTitle = originalTitle;
          incomingOriginalLink.dataset.preserveFilters = 'true';
          incomingOriginalLink.classList.toggle('is-active', originalIsActive);

          if (originalIsActive) incomingOriginalLink.setAttribute('aria-current', 'page');
          else incomingOriginalLink.removeAttribute('aria-current');
        }

        const requestedHandle = this.collectionHandleFromUrl(requestUrl);
        this.normalizeCollectionChips(incoming, requestedHandle);

        this.innerHTML = incoming.innerHTML;

        if (preserveRailPosition) {
          const nextRail = this.querySelector('[data-occasion-rail]');
          if (nextRail) {
            const previousScrollBehavior = nextRail.style.scrollBehavior;
            nextRail.style.scrollBehavior = 'auto';
            nextRail.scrollLeft = railScrollLeft;
            requestAnimationFrame(() => {
              nextRail.scrollLeft = railScrollLeft;
              nextRail.style.scrollBehavior = previousScrollBehavior;
            });
          }
        }

        // Non-core collection templates: keep the mobile chip rail exactly where
        // the customer left it when AJAX swaps the collection results.
        // Collection Page 1/2/3 behavior is intentionally left unchanged.
        if (preserveShellRail && window.matchMedia('(max-width: 767px)').matches) {
          const nextRail = this.querySelector('[data-occasion-rail]');
          if (nextRail) {
            const previousScrollBehavior = nextRail.style.scrollBehavior;
            nextRail.style.scrollBehavior = 'auto';
            nextRail.scrollLeft = railScrollLeft;
            requestAnimationFrame(() => {
              nextRail.scrollLeft = railScrollLeft;
              nextRail.style.scrollBehavior = previousScrollBehavior;
            });
          }
        }

        this.sectionId = incoming.dataset.sectionId || this.sectionId;
        this.collectionUrl = incoming.dataset.collectionUrl || requestUrl.pathname;
        this.collectionHandle = incoming.dataset.collectionHandle || this.collectionHandleFromUrl(this.collectionUrl);
        this.dataset.sectionId = this.sectionId;
        this.dataset.collectionUrl = this.collectionUrl;
        this.dataset.collectionHandle = this.collectionHandle;
        this.loading = this.querySelector('[data-collection-loading]');
        this.status = this.querySelector('[data-collection-status]');
        this.currentRenderedParams = this.collectionParamsFromUrl(requestUrl).toString();
        document.documentElement.style.overflow = '';

        if (pushState) window.history.pushState({ collectionAjax: true }, '', publicUrl.toString());

        this.announce('Collection results updated.');
        this.scheduleAdjacentPrefetch();
        document.dispatchEvent(new CustomEvent('collection:updated', {
          detail: { url: publicUrl.toString(), collectionHandle: this.collectionHandle }
        }));
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(error);
          this.setActiveCollectionChip(this.collectionHandle);
          this.announce('The collection could not be updated. Please try again.');
        }
      } finally {
        this.setLoading(false);
      }
    }

    async loadMore(button) {
      const nextUrl = new URL(button.dataset.nextUrl, window.location.origin);
      const requestUrl = this.buildRequestUrl(nextUrl.pathname, this.collectionParamsFromUrl(nextUrl));
      button.disabled = true;
      button.classList.add('is-loading');

      try {
        const html = await this.getSectionHtml(requestUrl);
        const incoming = this.parseIncoming(html);
        const currentGrid = this.querySelector('[data-product-grid]');
        const existingIds = new Set([...this.querySelectorAll('[data-product-grid-item]')].map((item) => item.dataset.productId));

        incoming?.querySelectorAll('[data-product-grid-item]').forEach((item) => {
          if (!existingIds.has(item.dataset.productId)) currentGrid?.append(item);
        });

        const incomingFooter = incoming?.querySelector('[data-pagination-footer]');
        const currentFooter = this.querySelector('[data-pagination-footer]');
        if (currentFooter && incomingFooter) currentFooter.replaceWith(incomingFooter);
        this.announce('More cards loaded.');
      } catch (error) {
        console.error(error);
        button.disabled = false;
        button.classList.remove('is-loading');
        this.announce('More cards could not be loaded. Please try again.');
      }
    }

    openDrawer(trigger) {
      const drawer = this.querySelector('[data-mobile-filter-drawer]');
      if (!drawer) return;
      this.drawerTrigger = trigger;
      drawer.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      requestAnimationFrame(() => drawer.querySelector('[role="dialog"]')?.focus());
    }

    closeDrawer(restoreFocus = true) {
      const drawer = this.querySelector('[data-mobile-filter-drawer]');
      if (!drawer || drawer.hidden) return;
      drawer.hidden = true;
      document.documentElement.style.overflow = '';
      if (restoreFocus) this.drawerTrigger?.focus();
    }

    setLoading(active) {
      if (this.loading) this.loading.hidden = !active;
      this.setAttribute('aria-busy', active ? 'true' : 'false');
    }

    announce(message) {
      if (this.status) this.status.textContent = message;
    }
  });
}

if (!window.cardologyCollectionPopstateBound) {
  window.cardologyCollectionPopstateBound = true;
  window.addEventListener('popstate', () => {
    document.querySelectorAll('collection-ajax-experience').forEach((element) => element.restoreFromLocation());
  });
}

document.addEventListener('click', (event) => {
  document.querySelectorAll('collection-ajax-experience [data-filter-dropdown][open]').forEach((details) => {
    if (!details.contains(event.target)) details.removeAttribute('open');
  });
});
